import React, {FC, StrictMode, useEffect} from 'react';
import {createRoot} from 'react-dom/client';

import {FrontWindow, globalDocument} from '../../libs/shared/core/src/helpers/browser/browserHelpers';
import {parseQueryString} from '../../libs/shared/core/src/helpers/browser/urlHelpers';
import {isElectron} from '../../libs/shared/core/src/helpers/environment/platformHelpers';
import {logger} from '../../libs/shared/core/src/services/logger/logger';
import {ensureLoadedOnceInBrowsingContext} from '../services/loadedOnce';
import {withTimeoutAsync} from './asyncHelpers';

/*
 * Render.
 */

interface RenderFrontOptions {
  /** Component to render. */
  Component: FC;
  /** DOM element to render in. */
  domRoot: HTMLElement;
  /** Handler for when the component is first rendered. */
  onRender?: () => void;
  /** Handler for when the effects have been run. */
  onEffects?: () => void;
  /** Whether to enable React 18 strict mode. */
  hasReact18StrictMode?: boolean;
}

export function renderFront(options: RenderFrontOptions) {
  const {Component, domRoot, onRender, onEffects, hasReact18StrictMode = false} = options;

  const FrontWithCallback = () => {
    useEffect(() => {
      onRender?.();
      onEffects?.();
    });

    if (hasReact18StrictMode) {
      return (
        <StrictMode>
          <Component />
        </StrictMode>
      );
    }

    return <Component />;
  };

  const root = createRoot(domRoot);
  root.render(<FrontWithCallback />);
}

/*
 * Pre-flight.
 */

export function ensureSafeToLoadInWindow(window: FrontWindow) {
  // https://docs.google.com/document/d/1Uhhr96gIeYvIYzUbhT6UubTdyoMsrSZqLjK23CghLbE/edit
  ensureLoadedOnceInBrowsingContext(window);

  // Out of an abundance of caution; should be caught by ensureLoadedOnceInBrowsingContext().
  if (window.location.pathname === '/cards') {
    throw new Error('Refusing to serve Old Front /cards route, this is New Front!');
  }

  // https://github.com/frontapp/front-client/pull/4961
  const query = parseQueryString(window.location.search);
  if (query.serve === 'legacy') {
    throw new Error('Failed to serve Old Front, this is New Front!');
  }
}

/*
 * Loading overlay.
 */

const loadingOverlayLogger = logger.getLogger('loadingOverlay');
const hideLoadingOverlayTimeout = 3100;

export async function hideLoadingOverlayAsync() {
  const loadingElement = globalDocument.getElementById('front-client-loading');
  if (!loadingElement) {
    loadingOverlayLogger.error('Failed to find loading overlay element');
    return;
  }

  await withTimeoutAsync(
    () => hideLoadingOverlayElementAsync(loadingElement),
    hideLoadingOverlayTimeout,
    () => {
      throw new Error('Timed out while hiding loading overlay');
    },
  ).catch((error) => {
    loadingElement.remove();
    loadingOverlayLogger.error(error.message);
  });
}

async function hideLoadingOverlayElementAsync(loadingElement: Element) {
  // As of 08/10/2020: the Electron app has its own loading mechanism so waiting would cause a flicker.
  // TODO: remove this check when the Electron app gets rid of its own loading logo.
  if (!isElectron()) {
    await fadeOutLoadingOverlayAsync(loadingElement);
  }

  loadingElement.remove();
}

async function fadeOutLoadingOverlayAsync(loadingElement: Element) {
  const transitionEndPromise = maybeWaitForTransitionEndAsync(loadingElement);
  loadingElement.classList.remove('show');
  await transitionEndPromise;
}

async function maybeWaitForTransitionEndAsync(element: Element): Promise<void> {
  const hasTransitionEndEvent = 'ontransitionend' in element;
  if (!hasTransitionEndEvent) {
    return;
  }

  await new Promise((resolve) => {
    element.addEventListener('transitionend', resolve, {once: true});
  });
}
